import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, Circle, ArrowRight, X, User, Building2, BookOpen, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const gettingStartedSteps = [
  {
    id: 'profile',
    title: '🔰 Complete Your Profile',
    description: 'Add your business info and goals',
    link: '/dashboard',
    icon: <User className="h-4 w-4" />
  },
  {
    id: 'directory',
    title: '🔰 Explore the Directory',
    description: 'Discover Louisiana businesses',
    link: '/businesses',
    icon: <Building2 className="h-4 w-4" />
  },
  {
    id: 'resources',
    title: '🔰 Browse Resources',
    description: 'Find grants and learning materials',
    link: '/resources',
    icon: <BookOpen className="h-4 w-4" />
  },
  {
    id: 'ai',
    title: '🔰 Chat with AI Assistant',
    description: 'Get personalized business advice',
    link: '/ai-assistant',
    icon: <MessageSquare className="h-4 w-4" />
  }
];

function GettingStartedChecklist() {
  const { user } = useAuth();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Only show for logged-in users
    if (!user) return;

    // Check if user dismissed the checklist
    const dismissed = localStorage.getItem('checklistDismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Load completed steps from localStorage
    const saved = localStorage.getItem('completedSteps');
    if (saved) {
      setCompletedSteps(JSON.parse(saved));
    }

    // Show after a short delay
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [user]);

  const toggleStep = (stepId) => {
    const newCompleted = completedSteps.includes(stepId)
      ? completedSteps.filter(id => id !== stepId)
      : [...completedSteps, stepId];
    
    setCompletedSteps(newCompleted);
    localStorage.setItem('completedSteps', JSON.stringify(newCompleted));
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => setIsDismissed(true), 300);
    localStorage.setItem('checklistDismissed', 'true');
  };

  const progress = (completedSteps.length / gettingStartedSteps.length) * 100;

  if (!user || isDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: 300 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 300 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="fixed right-4 top-32 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-40 overflow-hidden"
          data-testid="getting-started-checklist"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#006847] to-[#A4D65E] p-4 text-white">
            <div className="flex items-center justify-between">
              <h3 className="font-bold">Getting Started</h3>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded transition"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm opacity-90">Complete these steps to get the most out of DowUrk</p>
            
            {/* Progress Bar */}
            <div className="mt-3 bg-white/20 rounded-full h-2 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className="bg-white h-full rounded-full"
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-xs mt-1 opacity-90">{completedSteps.length} of {gettingStartedSteps.length} complete</p>
          </div>

          {/* Steps */}
          <div className="p-4 space-y-3">
            {gettingStartedSteps.map((step) => {
              const isCompleted = completedSteps.includes(step.id);
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isCompleted ? 'bg-green-50' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <button
                    onClick={() => toggleStep(step.id)}
                    className="flex-shrink-0"
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-6 w-6 text-[#006847]" />
                    ) : (
                      <Circle className="h-6 w-6 text-gray-300 hover:text-[#A4D65E]" />
                    )}
                  </button>
                  <Link to={step.link} className="flex-1 min-w-0">
                    <p className={`font-medium text-sm ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{step.description}</p>
                  </Link>
                  <div className={`p-2 rounded-lg ${isCompleted ? 'bg-[#006847]/10' : 'bg-gray-200'}`}>
                    {step.icon}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {completedSteps.length === gettingStartedSteps.length && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-green-50 p-4 border-t border-green-100"
            >
              <p className="text-[#006847] font-medium text-center">
                🎉 You're all set! Welcome to DowUrk!
              </p>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default GettingStartedChecklist;
